// ─────────────────────────────────────────────────────────────────────────────
// NOTAS DE VERSÃO — o que mudou no Orion, contado para quem usa.
//
// Mesma ideia das notas do Nexus (Nexus/client/src/services/v2/notasVersao.js):
// quem atende o dia inteiro não lê commit nem release — abre o Orion e descobre,
// no meio de um atendimento, que o "Atender" passou a assumir o ticket ou que
// apareceu um bloco novo no resumo. Descobrir assim parece defeito.
//
// Cada mudança vem com o seu tipo (novidade, melhoria, correção) e os scripts
// de banco ficam num bloco à parte, no fim: eles não são assunto de todo mundo,
// são de quem aplica o banco.
//
// ⚠️ A VERSÃO É A CHAVE. A tela abre na nota da versão que está rodando
// (`versao` do contexto: no desktop vem do processo principal, na web do
// package.json embutido no build). Versão sem entrada aqui não inventa nada: a
// tela diz que a nota não foi escrita e mostra a última documentada. Então o
// bump do package.json e a nota nova andam juntos.
//
// COMO ACRESCENTAR UMA VERSÃO: um objeto novo no TOPO de `NOTAS_VERSAO`, no
// mesmo formato. O seletor, a ordenação, o destaque da versão atual e o aviso
// de "novidades" no topo saem daqui.
//
// DE ONDE VEIO O CONTEÚDO ATÉ A 2.0.2: do histórico do git, agrupado pelas
// trocas de versão do package.json — o Orion não publicava notas antes disto.
// ─────────────────────────────────────────────────────────────────────────────

// O TIPO DE CADA MUDANÇA — é o que o olho separa antes de ler.
// 'interno' é o que não muda nada na tela mas explica um comportamento novo do
// sistema. Fica por último de propósito.
export const TIPOS_MUDANCA = {
  novidade: { label: 'Novidade', classe: 'nv-novidade', icone: 'plus' },
  melhoria: { label: 'Melhoria', classe: 'nv-melhoria', icone: 'trending-up' },
  correcao: { label: 'Correção', classe: 'nv-correcao', icone: 'shield-check' },
  interno: { label: 'Ajuste interno', classe: 'nv-interno', icone: 'code' },
};

export const ORDEM_TIPOS = ['novidade', 'melhoria', 'correcao', 'interno'];

// ---------------------------------------------------------------------------
// AS NOTAS. Mais recente no topo.
//
// `data` é o dia da publicação (null para o que ainda não saiu).
// `scripts[].alteraDados` responde à pergunta que importa antes de rodar um SQL
// em produção: isto mexe no que já está gravado?
// ---------------------------------------------------------------------------
export const NOTAS_VERSAO = [
  {
    versao: '2.0.5',
    data: '2026-10-30',
    titulo: 'Notificações, pulo de fila com justificativa, mensagem privada e áudio no ticket novo',
    resumo: 'O Orion ganhou notificações, com sininho e caixa de entrada. Puxar um ticket com outros livres à frente agora pede justificativa, que a gerência apura na tela de Filas, e ao coletar o Orion oferece os outros tickets livres do mesmo cliente. No envio, dá para mandar o texto sem formalizar e marcar a mensagem como privada; o ticket novo aceita áudio. A tela de início ganhou o cartão "Outros status", e o resumo do ticket mostra as imagens anexadas no Nexus.',
    mudancas: [
      {
        tipo: 'novidade',
        titulo: 'Cartão "Outros status" na tela de início',
        texto: 'Mostra os tickets no seu nome que não estão em nenhum dos outros cartões: tudo o que não é concluído, não é do Setor de Sistemas (status "SIS - …" e "Em análise / Sistemas") e não é aguardando solução, em atendimento ou fila de retorno. Cada ticket da lista vem com o status em que está. Como o "Aguardando solução", o cartão só aparece quando tem ticket, em tom de atenção, e atualiza junto com os outros a cada 5 minutos.',
      },
      {
        tipo: 'novidade',
        titulo: 'Imagens do Nexus no resumo do ticket',
        texto: 'Os prints que o Setor de Sistemas anexa no Nexus não chegam ao MySuite — a API não aceita anexo —, e o ticket ficava com um "segue imagem em anexo" sem imagem nenhuma. Agora o resumo do ticket busca essas imagens no Nexus e as mostra numa aba "Anexos do Nexus", ao lado de "Interações" — no mesmo espaço da conversa, para não empurrá-la para fora da tela. Cada imagem vem com a origem (Causa, Solução, Tarefa 2 · Solução definitiva…); clique na miniatura para ampliar, e Esc fecha. As abas só aparecem quando o ticket tem imagem no Nexus, e o resumo abre sempre nas interações.',
      },
      {
        tipo: 'novidade',
        titulo: 'Pulo de fila: aviso e justificativa',
        texto: 'Ao puxar ou atender um ticket livre, o Orion confere se havia outros livres à frente dele na fila — na mesma ordem da tela de Filas: prioridade primeiro, depois o SLA. Havendo, aparece o aviso "Há 9 tickets à frente, incluindo uma Alta estourada", com a lista dos que ficam para trás, e o ticket só é puxado com uma justificativa. Pegar o 10º conta como 9 tickets pulados. Empate (mesma prioridade e mesmo SLA) não conta como pulo, e agendado cuja hora ainda não chegou também não.',
      },
      {
        tipo: 'novidade',
        titulo: 'Apuração dos pulos de fila, para a gerência',
        texto: 'Cada pulo justificado é gravado com quem puxou, o ticket, a posição dele na fila, os tickets que ficaram para trás e a justificativa. Na tela de Filas, o gerente tem o bloco "Pulos de fila", com a lista de cada justificativa, no dia, em 7 ou em 30 dias. Na apuração, ele registra o parecer e decide se o pulo é "Contabilizado" (entra nas apurações) ou "Não contabilizado" (a justificativa procede: fica registrado, mas não conta) — e pode rever a decisão depois. O resumo por operador soma pulos e tickets pulados só dos contabilizados, com os pendentes e os não contabilizados à parte.',
      },
      {
        tipo: 'novidade',
        titulo: 'Outros tickets do mesmo cliente ao coletar',
        texto: 'Depois de assumir um ticket, se houver outros livres do mesmo cliente na fila, o Orion mostra a lista e pergunta se você quer puxar todos para o seu nome. Dá para desmarcar alguns, ou ignorar a lista e ficar só com o ticket que já pegou — mas deixar qualquer ticket do cliente na fila pede uma justificativa, que vai para a gerência apurar no mesmo bloco dos pulos de fila, como "Mesmo cliente".',
      },
      {
        tipo: 'novidade',
        titulo: 'Notificações, com sininho e caixa de entrada',
        texto: 'O topo do Orion ganhou um sininho ao lado do seu nome, com o número de notificações por ler — igual ao do Nexus: clicar abre um painel com as mais recentes, e clicar numa delas abre o resumo do ticket e marca como lida. No pé do painel, "Ver caixa de entrada" leva à lista completa, que também está no rodapé do menu, em "Notificações", junto de Zarion, Configurações e Notas de versão. A caixa de entrada traz todas as notificações do operador logado, com o filtro "Não lidas" e o botão "Marcar todas como lidas"; ali também, clicar abre o ticket. Notificação nova chega na hora, com um aviso na tela que também abre o ticket. Por enquanto entram aqui o retorno de um ticket seu do setor de Sistemas e a decisão da gerência sobre um pulo de fila seu (contabilizado ou não, com o parecer); outros sistemas, como o n8n, também podem mandar notificações para o Orion.',
      },
      {
        tipo: 'novidade',
        titulo: 'Áudio na criação de ticket novo',
        texto: 'O campo "O que aconteceu" da criação de ticket novo ganhou o mesmo gravador do atendimento: o microfone no canto grava na hora, e o clipe anexa um arquivo de áudio (arrastar um áudio para o campo também funciona). Ao clicar em "Formalizar com IA", o áudio é transcrito e formalizado junto com o que estiver escrito — com áudio, o texto passa a ser opcional —, e o resultado entra no campo para você revisar. O Orion não deixa criar o ticket com um áudio ainda não transcrito, porque o MySuite recebe só o texto.',
      },
      {
        tipo: 'novidade',
        titulo: 'Enviar sem formalizar',
        texto: 'Na tela de Atendimento, ao lado de "Formalizar ticket", o botão "Enviar sem formalizar" leva o texto para a revisão do jeito que você escreveu, sem passar pela IA — imagens incluídas, no mesmo lugar. Para usar, o texto precisa ter ao menos 50 caracteres; o botão mostra quantos faltam. Formalizar continua sem mínimo. Como a IA não lê o texto, não vem recomendação de classificação: vale a do MySuite ou o ajuste manual.',
      },
      {
        tipo: 'novidade',
        titulo: 'Mensagem privada no envio do ticket',
        texto: 'O card do ticket formalizado ganhou, no canto superior direito, a opção "Mensagem privada": marcada, ela fica vermelha e a interação vai para o MySuite visível só para a equipe — o cliente não vê. A confirmação do envio lembra que a mensagem vai como privada, e a marcação volta a desligada a cada atendimento.',
      },
      {
        tipo: 'correcao',
        titulo: 'Listas de opções que abriam vazias',
        texto: 'Em alguns monitores, com o Orion na web em tela cheia e no tema escuro, as listas de opções (como o tipo de atendimento) abriam sem nada escrito: o navegador desenhava a lista em fundo branco, e o texto das opções, claro por causa do tema, sumia. Agora as listas usam as cores do tema em qualquer modo de exibição.',
      },
      {
        tipo: 'melhoria',
        titulo: 'Interação privada mais visível no resumo do ticket',
        texto: 'Na conversa do ticket, a interação privada agora tem o cartão em vermelho claro, com uma faixa vermelha à esquerda e o selo "PRIVADO" em vermelho; a pública continua na cor de quem escreveu. A coluna do autor mantém a cor dele nas duas, para continuar dando para ver quem registrou.',
      },
      {
        tipo: 'melhoria',
        titulo: 'Cards das notas de versão com rolagem',
        texto: 'Nesta tela, cada card (Novidade, Melhoria, Correção…) tem agora uma altura máxima: quando a versão traz muitos itens, eles rolam por dentro do card, com o título do grupo fixo em cima, em vez de esticar o card e empurrar os outros para baixo.',
      },
    ],
    scripts: [
      {
        arquivo: 'criar-tabela_ticket-pulo_20261029_1100.sql',
        alteraDados: false,
        resumo: 'Cria `orion.ticket_pulo`, onde ficam os pulos de fila justificados e a apuração da gerência, com as políticas de leitura, gravação e atualização para o app. Sem ele, o Orion continua pedindo a justificativa, mas avisa que o registro não foi gravado e o bloco da gerência fica vazio.',
      },
      {
        arquivo: 'alterar-tabela_ticket-pulo-contabiliza_20261029_1500.sql',
        alteraDados: false,
        resumo: 'Acrescenta a coluna `contabiliza` em `orion.ticket_pulo` — a decisão da gerência de contar ou não o pulo nas apurações. Rodar depois do script acima. Só cria a coluna, vazia; sem ela, a apuração na tela de Filas é recusada com o aviso de qual script falta.',
      },
      {
        arquivo: 'alterar-tabela_ticket-pulo-mesmo-cliente_20261029_1630.sql',
        alteraDados: false,
        resumo: 'Acrescenta a coluna `tipo` em `orion.ticket_pulo` (pulo ou mesmo_cliente) e torna `posicao` opcional, para gravar a justificativa de quem deixa na fila tickets livres do mesmo cliente. Os registros existentes recebem tipo "pulo", que é o que eles são; nenhum outro dado muda. Rodar depois dos dois scripts acima.',
      },
      {
        arquivo: 'criar-tabela_notificacao_20261029_1800.sql',
        alteraDados: false,
        resumo: 'Cria `orion.notificacao`, a caixa de entrada do sininho, com as políticas de leitura, gravação e atualização para o app, e a inclui na publicação do realtime para a notificação chegar na hora. Sem ele, a tela de Notificações avisa que falta este script e o sininho fica sem número.',
      },
    ],
  },
  {
    versao: '2.0.4',
    data: '2026-10-23',
    titulo: 'Tipo de atendimento "Agendado" — e o aviso de "ticket de outro operador" volta a dizer a verdade',
    resumo: 'Quando o cliente pede para ser atendido em outro dia e horário, a formalização agora tem o tipo "Agendado", com a data e a hora combinadas. E o Orion deixa de acusar ticket livre como sendo de um colega: ele guardava a fila de pendentes na primeira consulta da sessão e não a renovava mais.',
    mudancas: [
      {
        tipo: 'novidade',
        titulo: 'Tipo de atendimento "Agendado"',
        texto: 'Na formalização, o tipo de atendimento ganhou a opção "Agendado". Escolhida, aparecem logo abaixo a data e a hora da previsão, já preenchidas com a previsão que o ticket tem, para trocar pela que o cliente pediu. No envio, o ticket vai para o status "Agendado" no MySuite com essa previsão e volta para a fila da Análise Suporte, de onde reaparece para o time no horário agendado. Data e hora são obrigatórias, e uma previsão que já passou é recusada.',
      },
      {
        tipo: 'correcao',
        titulo: 'A previsão vai no formato do MySuite',
        texto: 'A data e a hora da previsão passam a ser enviadas como o MySuite grava — "23/10/2026" e "12:00". No formato antigo ("2026-10-23"), o MySuite trocava o status e mantinha a previsão anterior: foi o que aconteceu no primeiro teste do tipo "Agendado". Vale para todo envio da formalização que leva previsão, como "Atualização" e "Na fila para retorno". O "Contato sem sucesso" não muda: ele já agendava certo.',
      },
      {
        tipo: 'correcao',
        titulo: '"Está em atendimento com outro operador" em ticket livre',
        texto: 'No ticket 352067, o Caio Cunha atendeu das 09:18 às 09:34 e devolveu o ticket à Análise Suporte. Às 09:56, ao abrir o ticket, a Joyce Almeida foi avisada de que ele estava em atendimento com o Caio. Agora o Orion relê a fila na hora de conferir de quem é o ticket — ao abrir na formalização e ao clicar em "Atender" — e só avisa quando a fila e o próprio ticket no MySuite apontam o mesmo colega. A mesma falha podia recusar o "Atender" de um ticket livre ou deixar passar um que tinha acabado de ganhar dono.',
      },
    ],
    scripts: [],
    nota: '',
  },
  {
    versao: '2.0.3',
    data: '2026-10-22',
    titulo: 'Filas pela prioridade, o "Atender" sem falso erro — e o cliente com observações à vista',
    resumo: 'As filas passam a mostrar primeiro o que é mais urgente: prioridade Alta, depois Média, depois Baixa, e dentro de cada uma o SLA. O MySuite grava a troca de status na hora, mas as consultas da API só mostram a mudança em ciclos de um a dois minutos. O Orion conferia logo depois de marcar o ticket, lia o status antigo e avisava "a marcação não pegou" — com o ticket já em atendimento com você. Agora o Orion pede ao MySuite a carga do dia antes de conferir e antes de atualizar as filas. Junto vêm as observações do cadastro do cliente (dados de conexão, acessos) a um clique, no resumo do ticket e na formalização.',
    mudancas: [
      {
        tipo: 'novidade',
        titulo: 'Filas ordenadas pela prioridade do ticket',
        texto: 'Os cartões da Minha Área e as listas do painel de filas do setor passam a vir na ordem da prioridade cadastrada no MySuite — Alta, Média, Baixa — e, dentro da mesma prioridade, pelo SLA: a previsão mais próxima ou já vencida primeiro. Cada ticket mostra a prioridade por extenso numa etiqueta e na barra lateral do card, em vermelho (Alta), laranja (Média) ou verde (Baixa). Ticket sem prioridade vem depois dos de prioridade Baixa. O alerta de horário da fila de retorno continua avisando pelo horário marcado.',
      },
      {
        tipo: 'novidade',
        titulo: 'Observações do cliente no ticket',
        texto: 'O resumo do ticket e o painel do ticket na formalização ganharam o bloco "Observações do cliente". Ele começa fechado e, ao clicar, mostra o campo de observação do cadastro do cliente no MySuite, buscado pelo CNPJ do ticket — onde ficam dados de conexão, acessos e particularidades. O texto aparece como foi digitado, com as quebras de linha, e pode ser selecionado para copiar. Cliente sem observação mostra "Este cliente não tem observações cadastradas"; falha de consulta oferece "Tentar de novo".',
      },
      {
        tipo: 'melhoria',
        titulo: '"Atualizar" traz o que acabou de mudar no MySuite',
        texto: 'O botão "Atualizar" das filas da tela inicial passa a pedir ao MySuite a carga dos tickets do dia antes de consultar, e repete a leitura alguns segundos depois para pegar o que a carga trouxe. Antes ele podia devolver a mesma foto de minutos atrás.',
      },
      {
        tipo: 'melhoria',
        titulo: 'Ícone de atualizar gira enquanto atualiza',
        texto: 'Em todas as telas com botão de atualizar (filas, Histórico, Configurações, Gerência, Zarion), o ícone de setinhas gira enquanto a consulta está rodando e o botão fica desabilitado até terminar. Nas filas ele também gira durante a atualização automática de 5 em 5 minutos.',
      },
      {
        tipo: 'melhoria',
        titulo: 'As filas se atualizam assim que você assume um ticket',
        texto: 'Depois de "Atender", os cartões da tela inicial recarregam na hora: o ticket sai de "Setor de sistemas" ou da fila de retorno e aparece em "Em atendimento", sem esperar a próxima atualização automática.',
      },
      {
        tipo: 'correcao',
        titulo: '"A marcação não pegou" em ticket que tinha sido assumido',
        texto: 'Ao atender tickets da fila de retorno e do Setor de Sistemas, o Orion marcava o ticket corretamente no MySuite mas mostrava o erro "A marcação não pegou" e não abria a formalização. A conferência lia o ticket antes de a API refletir a mudança. Agora ela pede a carga do dia ao MySuite e relê o ticket por até cerca de 20 segundos antes de concluir que a marcação falhou.',
      },
    ],
    scripts: [],
    nota: '',
  },
  {
    versao: '2.0.2',
    data: '2026-10-13',
    titulo: 'A correção que volta do setor de Sistemas chega avisando',
    resumo: 'Quando o setor de Sistemas corrigia um problema e o chamado voltava para o operador, ele caía na fila de retorno como qualquer agendamento, sem dizer que havia uma atualização esperando para ser aplicada no cliente. E, depois de o cliente ser atualizado, alguém ainda precisava abrir o Pulse para encerrar a demanda. Agora o Orion avisa o retorno e encerra a demanda sozinho quando o atendimento é concluído.',
    mudancas: [
      {
        tipo: 'novidade',
        titulo: 'Aviso de ticket que voltou do setor de Sistemas',
        texto: 'O Orion consulta os incidentes devolvidos a você e avisa uma vez por devolução, mesmo com a janela minimizada. Ao abrir o ticket na formalização, aparece "Voltou do setor de Sistemas" com a informação da correção — é uma atualização para agendar com o cliente, não um retorno comum.',
      },
      {
        tipo: 'novidade',
        titulo: 'Concluir o atendimento encerra a demanda no setor de Sistemas',
        texto: 'Ao concluir no Orion o atendimento de um ticket que voltou com a correção pronta, o incidente é concluído no Pulse e a solicitação é fechada no Nexus, sem precisar abrir outro sistema. Se o encerramento falhar, o atendimento continua enviado e o Orion avisa para pedir ao suporte avançado que feche a demanda no Pulse.',
      },
      {
        tipo: 'correcao',
        titulo: 'O sistema do incidente vem da categoria do ticket',
        texto: 'O sistema do incidente vinha do palpite da IA sobre o texto do atendimento — no ticket 351357, um chamado de "Space Guardian" foi registrado como "Space Gemini" e a demanda foi para o time errado. Agora, quando a categoria do ticket corresponde a um sistema cadastrado, ela vence a sugestão da IA. A escolha feita por você continua valendo acima das duas.',
      },
    ],
    scripts: [
      {
        arquivo: 'hotfix-historico-status_orion-log-mudanca-status-definer_20261013_2005.sql',
        alteraDados: false,
        resumo: 'Torna `orion.log_mudanca_status()` SECURITY DEFINER. Sem ele, concluir pelo Orion um ticket que voltou do setor de Sistemas é recusado com 401 e a demanda não é encerrada. Troca só o corpo de uma função; nenhuma linha é alterada.',
      },
    ],
    nota: '',
  },
  {
    versao: '2.0.1',
    data: '2026-09-29',
    titulo: 'Filas do setor, consulta de tickets e atualização automática',
    resumo: 'O suporte ganhou a visão do setor inteiro, com o que está livre para pegar, e o gerente ganhou o painel de plantão. A consulta de tickets por número e o filtro avançado chegaram com exportação. E o aplicativo desktop passou a se atualizar sozinho.',
    mudancas: [
      {
        tipo: 'novidade',
        titulo: 'Filas do setor',
        texto: 'Todo o suporte vê as filas do setor, com o nome de quem está com cada ticket, e pode puxar um ticket livre. "Aguardando solução" mostra só a Análise Suporte, e o Setor de Sistemas fica fora da apuração de atraso. A tela "Formalização" passou a se chamar "Minha Área".',
      },
      {
        tipo: 'novidade',
        titulo: 'Painel de plantão para a gerência',
        texto: 'Quem é gerente do suporte vê o painel de plantão, com as filas e o estado dos outros operadores. Toda métrica abre a lista correspondente, e cada ticket abre o resumo.',
      },
      {
        tipo: 'novidade',
        titulo: 'Consultar ticket e filtro avançado',
        texto: 'A nova tela "Consultar ticket" abre a ficha e as interações de qualquer ticket pelo número. O filtro avançado lista tickets por cliente, usuário e outros campos, com busca nos dropdowns, e exporta o relatório em planilha e PDF com a identidade do Orion.',
      },
      {
        tipo: 'novidade',
        titulo: 'Atualização automática no desktop',
        texto: 'O Orion desktop baixa as versões novas sozinho e avisa pelo ícone de nuvem no topo quando há uma pronta para instalar. O mesmo ícone permite "Verificar atualizações" na hora.',
      },
      {
        tipo: 'melhoria',
        titulo: 'Lembrete da pausa marcada',
        texto: 'Quem esquece de voltar da pausa marcada recebe um lembrete, inclusive na versão web. O alerta da fila de retorno sai uma vez e, passado o horário, o ticket aparece como "excedido".',
      },
      {
        tipo: 'correcao',
        titulo: 'Solicitante de um ticket aparecia no ticket seguinte',
        texto: 'O nome do solicitante de um atendimento continuava no campo e ia parar na formalização do ticket seguinte. Agora o campo é limpo sempre que o número do ticket muda, e um solicitante não encontrado fica em branco com aviso.',
      },
      {
        tipo: 'correcao',
        titulo: 'Ociosidade e documentos',
        texto: 'A ociosidade deixou de ser registrada em cima de quem estava atendendo e de somar os 25 minutos iniciais duas vezes. O PDF do incidente deixou de cortar texto, e a versão exibida na web passou a ser a do pacote, não um número fixo.',
      },
    ],
    scripts: [],
    nota: '',
  },
  {
    versao: '2.0.0',
    data: '2026-08-24',
    titulo: 'O Orion 2.0 — para a empresa toda',
    resumo: 'O Orion foi reescrito e deixou de ser só do suporte: cada setor vê as telas que fazem sentido para ele. O clique num ticket passou a abrir um resumo, e o "Atender" passou a assumir o ticket no MySuite.',
    mudancas: [
      {
        tipo: 'novidade',
        titulo: 'Telas por setor',
        texto: 'O setor do seu cadastro decide o que aparece no menu. O suporte vê tudo; os demais setores veem Formalização rápida, Chamado T.I., Zarion e Configurações. Mudanças no cadastro chegam sem precisar sair e entrar de novo.',
      },
      {
        tipo: 'novidade',
        titulo: 'Resumo do ticket e "Atender" que assume',
        texto: 'Clicar num card abre o resumo do ticket: dados, situação e interações. "Atender" coloca o ticket em atendimento com você no MySuite antes de abrir a formalização.',
      },
      {
        tipo: 'novidade',
        titulo: 'Aba Atividade',
        texto: 'O histórico do seu status e das suas pausas, com a nova regra de ociosidade baseada em ter ou não ticket em atendimento.',
      },
      {
        tipo: 'melhoria',
        titulo: 'Filas que se atualizam sozinhas',
        texto: 'As filas do MySuite na tela inicial atualizam a cada 5 minutos, em qualquer aba, e a fila de retorno fica sempre à vista, com o horário quando ele se aproxima.',
      },
      {
        tipo: 'melhoria',
        titulo: 'Documentos e Zarion',
        texto: 'O documento do ticket concluído ficou mais claro e respeita a ordem das imagens. O documento do incidente e o Zarion abrem dentro do próprio Orion.',
      },
    ],
    scripts: [],
    nota: '',
  },
];

// Compara duas versões "x.y.z". Devolve > 0 quando `a` é mais nova.
// Comparar como texto diria que a 2.0.10 é anterior à 2.0.9.
export function compararVersoes(a, b) {
  const pa = String(a || '').replace(/^v/i, '').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '').replace(/^v/i, '').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
}

/** "v2.0.2", " 2.0.2 " → "2.0.2". */
export function normalizarVersao(versao) {
  return String(versao || '').trim().replace(/^v/i, '');
}

/** Da mais nova para a mais antiga, ordenando de verdade e não pela ordem do arquivo. */
export function notasOrdenadas() {
  return [...NOTAS_VERSAO].sort((a, b) => compararVersoes(b.versao, a.versao));
}

/** As notas desta versão, ou `null` quando ela não tem nota escrita. */
export function notasDaVersao(versao) {
  const alvo = normalizarVersao(versao);
  if (!alvo) return null;
  return NOTAS_VERSAO.find((n) => normalizarVersao(n.versao) === alvo) || null;
}

/** A nota mais recente que existe — o que a tela mostra quando a instalada não tem nota. */
export function notaMaisRecente() {
  return notasOrdenadas()[0] || null;
}

/** Mudanças agrupadas por tipo, na ordem de leitura, só os grupos com conteúdo. */
export function mudancasPorTipo(nota) {
  return ORDEM_TIPOS
    .map((tipo) => ({ tipo, itens: (nota?.mudancas || []).filter((m) => m.tipo === tipo) }))
    .filter((g) => g.itens.length);
}

/**
 * Data da publicação em pt-BR, ou '' quando a versão ainda não saiu.
 * Monta à mão: `new Date('2026-10-13')` é UTC e, no fuso de Brasília, voltaria
 * um dia.
 */
export function dataPublicacao(nota) {
  const [a, m, d] = String(nota?.data || '').split('-');
  return a && m && d ? `${d}/${m}/${a}` : '';
}

// ── "Novidades" no topo ──────────────────────────────────────────────────────
// A versão cujas notas este navegador já abriu. Enquanto a versão em uso tiver
// nota e ela não tiver sido aberta, o número da versão no topo ganha um ponto —
// é assim que quem acabou de receber a atualização fica sabendo que há o que
// ler. Por navegador, de propósito: é conveniência, não registro.

const CHAVE_VISTA = 'orion:notas-vistas';

export function notasJaVistas(versao) {
  try {
    return localStorage.getItem(CHAVE_VISTA) === normalizarVersao(versao);
  } catch (e) {
    return true;   // sem armazenamento, melhor não acender um ponto que nunca apaga
  }
}

export function marcarNotasVistas(versao) {
  try { localStorage.setItem(CHAVE_VISTA, normalizarVersao(versao)); } catch (e) { /* silencioso */ }
}
